import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import {
  createContext,
  forwardRef,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  FocusScope,
  mergeProps,
  useFocusManager,
  useHover,
  useKeyboard,
  useOverlay,
  usePreventScroll,
} from 'react-aria';

import isChildOf from '../../../../../../app/shared/isChildOf';
import css from './NavDropdown.css';

const CLOSE_DELAY_MS = 150;

export interface NavDropdownPanelRef {
  focusFirst: () => void;
}

interface NavDropdownContextValue {
  activeDropdownKey: number | null;
  cancelScheduledClose: () => void;
  close: () => void;
  getTriggerId: (itemKey: number) => string;
  isItemOpen: (itemKey: number) => boolean;
  panelId: string;
  panelRef: RefObject<NavDropdownPanelRef | null>;
  open: (itemKey: number) => void;
  registerTriggerElement: (itemKey: number, element: HTMLButtonElement | null) => void;
  scheduleClose: () => void;
  shouldCloseOnInteractOutside: (element: Element) => boolean;
  toggle: (itemKey: number) => void;
}

const NavDropdownContext = createContext<NavDropdownContextValue | null>(null);

function useNavDropdownContext() {
  const context = useContext(NavDropdownContext);

  if (!context) {
    throw new Error('NavDropdown components must be used within NavDropdownRoot.');
  }

  return context;
}

interface NavDropdownRootProps {
  activeDropdownKey: number | null;
  children: ReactNode;
  onActiveDropdownKeyChange: (key: number | null) => void;
}

export function NavDropdownRoot({ activeDropdownKey, children, onActiveDropdownKeyChange }: NavDropdownRootProps) {
  const id = useId();
  const panelId = `${id}-panel`;

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const triggerElementsRef = useRef<Record<string, HTMLButtonElement | null>>({});
  const panelRef = useRef<NavDropdownPanelRef>(null);

  const activeTriggerElement = activeDropdownKey !== null ? triggerElementsRef.current[activeDropdownKey] : null;

  function getTriggerId(itemKey: number) {
    return `${id}-${itemKey}-trigger`;
  }

  function registerTriggerElement(itemKey: number, element: HTMLButtonElement | null) {
    triggerElementsRef.current[itemKey] = element;
  }

  function cancelScheduledClose() {
    clearTimeout(closeTimeoutRef.current);
  }

  function open(itemKey: number) {
    cancelScheduledClose();
    onActiveDropdownKeyChange(itemKey);
  }

  function close() {
    cancelScheduledClose();
    onActiveDropdownKeyChange(null);
  }

  function toggle(itemKey: number) {
    if (activeDropdownKey === itemKey) {
      close();
    } else {
      open(itemKey);
    }
  }

  function scheduleClose() {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(close, CLOSE_DELAY_MS);
  }

  function shouldCloseOnInteractOutside(element: Element) {
    return !activeTriggerElement || !isChildOf(element as HTMLElement, activeTriggerElement);
  }

  function isItemOpen(itemKey: number) {
    return activeDropdownKey === itemKey;
  }

  useEffect(() => {
    return () => cancelScheduledClose();
  }, []);

  return (
    <NavDropdownContext.Provider
      value={{
        activeDropdownKey,
        cancelScheduledClose,
        close,
        getTriggerId,
        isItemOpen,
        open,
        panelId,
        panelRef,
        registerTriggerElement,
        scheduleClose,
        shouldCloseOnInteractOutside,
        toggle,
      }}
    >
      {children}
    </NavDropdownContext.Provider>
  );
}

interface NavDropdownTriggerProps {
  children: ReactNode;
  itemKey: number;
  className?: string;
}

export const NavDropdownTrigger = forwardRef<HTMLButtonElement, NavDropdownTriggerProps>(
  ({ children, itemKey, className }: NavDropdownTriggerProps, ref) => {
    const { getTriggerId, isItemOpen, open, panelId, panelRef, registerTriggerElement, scheduleClose, toggle } =
      useNavDropdownContext();

    const isOpen = isItemOpen(itemKey);

    const { hoverProps } = useHover({
      onHoverStart: () => open(itemKey),
      onHoverEnd: scheduleClose,
    });

    const { keyboardProps } = useKeyboard({
      onKeyDown: isOpen
        ? (event: KeyboardEvent) => {
            if (event.key === 'Tab' && !event.shiftKey) {
              event.preventDefault();
              panelRef.current?.focusFirst();
            }
          }
        : undefined,
    });

    function setTriggerRef(element: HTMLButtonElement | null) {
      registerTriggerElement(itemKey, element);

      if (typeof ref === 'function') {
        ref(element);

        return;
      }

      if (ref) {
        (ref as MutableRefObject<HTMLButtonElement | null>).current = element;
      }
    }

    return (
      <button
        {...mergeProps(hoverProps, keyboardProps, { onClick: () => toggle(itemKey) })}
        ref={setTriggerRef}
        id={getTriggerId(itemKey)}
        type="button"
        className={className}
        aria-controls={isOpen ? panelId : undefined}
        aria-expanded={isOpen}
      >
        {children}
      </button>
    );
  },
);

interface NavDropdownViewportProps {
  children: ReactNode;
}

export function NavDropdownViewport({ children }: NavDropdownViewportProps) {
  const { activeDropdownKey } = useNavDropdownContext();

  return <AnimatePresence>{activeDropdownKey !== null && children}</AnimatePresence>;
}

interface NavDropdownBackdropProps {
  className?: string;
}

export function NavDropdownBackdrop({ className }: NavDropdownBackdropProps) {
  return <motion.div className={className} initial={{ opacity: 0 }} animate={{ opacity: 0.2 }} exit={{ opacity: 0 }} />;
}

interface NavDropdownPanelProps {
  children: ReactNode;
  className?: string;
}

export function NavDropdownPanel({ children, className }: NavDropdownPanelProps) {
  const {
    activeDropdownKey,
    cancelScheduledClose,
    close,
    getTriggerId,
    panelId,
    scheduleClose,
    shouldCloseOnInteractOutside,
  } = useNavDropdownContext();
  const contentElementRef = useRef<HTMLDivElement>(null);

  const [height, setHeight] = useState(0);

  const isOpen = activeDropdownKey !== null;

  const { hoverProps } = useHover({
    onHoverStart: cancelScheduledClose,
    onHoverEnd: scheduleClose,
  });
  const { overlayProps } = useOverlay(
    {
      isDismissable: true,
      isOpen,
      shouldCloseOnBlur: true,
      shouldCloseOnInteractOutside,
      onClose: close,
    },
    contentElementRef,
  );
  usePreventScroll({ isDisabled: !isOpen });

  useEffect(() => {
    function updateHeight() {
      if (contentElementRef.current) {
        setHeight(contentElementRef.current.getBoundingClientRect().height);
      }
    }

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const rect = entry.target.getBoundingClientRect();

        setHeight(rect.height);
      }
    });

    if (contentElementRef.current) {
      updateHeight();
      observer.observe(contentElementRef.current);
    }

    return () => observer.disconnect();
  }, [children]);

  return (
    <motion.div
      role="region"
      id={panelId}
      className={css.content}
      aria-labelledby={activeDropdownKey !== null ? getTriggerId(activeDropdownKey) : undefined}
      initial={{ height: 0, opacity: 1 }}
      animate={{ height, opacity: 1, transition: { duration: 0.25, ease: 'easeOut' as const } }}
      exit={{ height: 0, opacity: 0, transition: { duration: 0.25, ease: 'easeIn' as const } }}
    >
      <div className={classNames(css.panelInner, className)}>
        <FocusScope restoreFocus>
          <NavDropdownPanelContent ref={contentElementRef} {...mergeProps(overlayProps, hoverProps)}>
            {children}
          </NavDropdownPanelContent>
        </FocusScope>
      </div>
    </motion.div>
  );
}

const animationVariants = {
  hidden: { y: -16, opacity: 0, transition: { duration: 0.2, ease: 'easeIn' as const } },
  visible: { y: 0, opacity: 1, transition: { duration: 0.2, delay: 0.1, ease: 'easeOut' as const } },
};

const NavDropdownPanelContent = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>((props, ref) => {
  const { activeDropdownKey, panelRef } = useNavDropdownContext();
  const focusManager = useFocusManager();
  const { children, className, ...restProps } = props;

  useImperativeHandle(
    panelRef,
    () => ({
      focusFirst() {
        focusManager.focusFirst();
      },
    }),
    [focusManager, panelRef],
  );

  return (
    <div ref={ref} {...restProps} className={className}>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          variants={animationVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          key={activeDropdownKey}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
});
